import classNames from 'classnames';
import React, { useEffect, useState, useRef } from 'react';
import {WebUI} from "../../wui-core/src/index";
import {
    detectFlexGapSupported,
    responsiveArray as DEVICE_SIZES,
    responsiveObserve,
    ScreenMap
} from "../../wui-core/src/responsiveObserve";
import RowContext from "./RowContext";
import { RowProps } from './iLayout';

const defaultProps: RowProps = {
    componentClass: 'div',
    grid: 24,
    wrap: true,
    gutter: 0,
};

const Row: React.FC<RowProps> = (props) => {
    const [supportFlexGap, setSupportFlexGap] = useState(true);
    const [screens, setScreens] = useState<ScreenMap>({
        xs: true,
        sm: true,
        md: true,
        lg: true,
        xl: true,
        xxl: true
    });
    const tokenRef = useRef<number | null>(null);

    useEffect(() => {
        setSupportFlexGap(detectFlexGapSupported());
        tokenRef.current = responsiveObserve.subscribe((screen: ScreenMap) => {
            setScreens(screen);
        }, props.size);

        return () => {
            if (tokenRef.current) {
                responsiveObserve.unsubscribe(tokenRef.current, props.size);
            }
        };
    }, [props.size]);

    const {
        componentClass = 'div',
        grid = 24,
        clsPrefix,
        gutter = 0,
        wrap = true,
        align,
        justify,
        className,
        style,
        children,
        ...others
    } = props;

    const Component = componentClass;

    const getGutter = (): [number, number] => {
        const results: [number, number] = [0, 0];
        const normalizedGutter = Array.isArray(gutter) ? gutter : [gutter, 0];
        normalizedGutter.forEach((g, index) => {
            if (typeof g === 'object') {
                for (let i = 0; i < DEVICE_SIZES.length; i++) {
                    const breakpoint = DEVICE_SIZES[i];
                    if (screens[breakpoint] && g[breakpoint] !== undefined) {
                        results[index] = g[breakpoint] || 0;
                        break;
                    }
                }
            } else {
                results[index] = g || 0;
            }
        });
        return results;
    };

    const gutters = getGutter();
    const classes = classNames(
        clsPrefix,
        {
            [`${clsPrefix}-no-wrap`]: wrap === false,
            [`${clsPrefix}-${justify}`]: justify,
            [`${clsPrefix}-${align}`]: align,
        }
    );

    // Add gutter related style
    const rowStyle: React.CSSProperties = {};
    const horizontalGutter = gutters[0] > 0 ? gutters[0] / -2 : undefined;
    const verticalGutter = gutters[1] > 0 ? gutters[1] / -2 : undefined;

    if (horizontalGutter) {
        rowStyle.marginLeft = horizontalGutter;
        rowStyle.marginRight = horizontalGutter;
    }

    if (supportFlexGap) {
        // Set gap direct if flex gap support
        [, rowStyle.rowGap] = gutters;
    } else if (verticalGutter) {
        rowStyle.marginTop = verticalGutter;
        rowStyle.marginBottom = verticalGutter;
    }

    const rowContext = {gutter: gutters, wrap, supportFlexGap};
    const extralClass = grid === 12 ? `${clsPrefix}-${grid}` : "";

    return (
        <RowContext.Provider value={{
            ...rowContext,
            screens
        }}>
            <Component
                {...others}
                className={classNames(classes, className, extralClass)}
                style={{...rowStyle, ...style}}
            >
                {children}
            </Component>
        </RowContext.Provider>
    );
};

export default WebUI({name: "row", defaultProps})(Row);